import { useCallback, useEffect, useRef, useState } from 'react';

// ── Types ──────────────────────────────────────────────────────────────────────

export type RiskStage =
  | 'idle'
  | 'risk_company_extraction'
  | 'risk_dimension_extraction'
  | 'risk_dimension_merge'
  | 'risk_priority_filter'
  | 'risk_dimension_comparison'
  | 'risk_question_merge'
  | 'risk_document_resolution'
  | 'risk_re_extraction'
  | 'awaiting_hitl'
  | 'risk_dimension_analysis'
  | 'risk_score_aggregation'
  | 'done'
  | 'retry_scheduled'
  | 'error';

export interface AreaProcessed {
  area: string;
  cached: boolean;
}

export interface StepperNode {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'complete' | 'failed';
  attempts?: number;
}

export interface RiskQuestion {
  question_id: string;
  dimension_id: string;
  policy_id: string;
  element_id?: string;
  question_text: string;
  question_type?: 'document_request' | 'standalone_question' | null;
  document_type?: string | null;
  document_description?: string | null;
  resolves_question_ids?: string[] | null;
  resolves_dimension_ids?: string[] | null;
}

export interface RiskDimensionResult {
  dimension_id: string;
  status: string;
  evidence_used: string[];
  residual_risk: string | null;
  likelihood: string;
  impact: string;
  control_effectiveness: number;
  risk_score: number;
  severity: string | null;
  recommendation: string | null;
  question?: string;
  area?: string;
}

export interface RiskAreaScore {
  risk_score: number;
  risk_rating: string;
  avg_likelihood: string;
  avg_impact: string;
  avg_control_effectiveness: number;
  dimensions_mitigated: number;
  dimensions_partially_mitigated: number;
  dimensions_unmitigated: number;
  top_residual_risks: string[];
}

export interface PolicyCoverage {
  raw_dimensions: number;
  covered: number;
  mitigated: number;
  partial: number;
  unmitigated: number;
}

export interface DeferredSummary {
  total: number;
  by_area: Record<string, number>;
}

export interface MergeStats {
  raw_count: number;
  post_dedup_count: number;
  post_filter_count?: number;
  deferred_count?: number;
  per_area_counts: Record<string, number>;
  deferred_by_area?: Record<string, number>;
}

export interface ResolutionStats {
  total_raw_questions: number;
  resolved_by_existing_docs: number;
  resolved_by_uploaded_docs: number;
  resolved_by_text_answers: number;
  document_requests_sent: number;
  standalone_questions_sent: number;
}

export interface RiskFinalResult {
  company_id: string;
  session_id: string;
  engine: string;
  overall_risk_score: number;
  health_rating: string;
  /** Real average control effectiveness across all dimensions (0–1). */
  avg_control_effectiveness?: number;
  area_risk_scores: Record<string, RiskAreaScore>;
  dimension_results: RiskDimensionResult[];
  risk_heatmap: Record<string, Record<string, number>>;
  critical_risks: (RiskDimensionResult & { question: string; area: string })[];
  top_recommendations: { recommendation: string; severity: string | null; risk_score: number; dimension_id: string; question: string }[];
  evidence_map: Record<string, { question: string; evidence_used: string[]; doc_sources: string[] }>;
  assessed_at: string;
  hitl_iterations: number;
  total_elements: number;
  elements_met: number;
  elements_partial: number;
  elements_missing: number;
  coverage_score: number;
  policy_coverage?: Record<string, PolicyCoverage>;
  deferred_summary?: DeferredSummary;
  merge_stats?: MergeStats;
  resolution_stats?: ResolutionStats;
}

export interface NodeStat {
  done: number;
  total: number;
  extra?: Record<string, number>;
}

export interface RiskSocketState {
  stage: RiskStage;
  stageMessage: string;
  sessionId: string;
  stepperNodes: StepperNode[];
  areasProcessed: AreaProcessed[];
  dimensionsExtracted: number;
  comparisonProgress: string;
  totalDimensions: number;
  questionsCount: number;
  questions: RiskQuestion[];
  finalResult: RiskFinalResult | null;
  error: string | null;
  eventLog: string[];
  nodeStats: Record<string, NodeStat>;
  /** Persisted-progress extras (SRS #122/#123) */
  attempts: number;
  maxAttempts: number;
  retryAfter: string | null;
  reconnecting: boolean;
}

const DEFAULT_STEPPER: StepperNode[] = [
  { id: 'risk_company_extraction', label: 'Extracting risk evidence', status: 'pending' },
  { id: 'risk_dimension_extraction', label: 'Extracting risk dimensions', status: 'pending' },
  { id: 'risk_dimension_merge', label: 'Deduplicating dimensions', status: 'pending' },
  { id: 'risk_priority_filter', label: 'Filtering by priority', status: 'pending' },
  { id: 'risk_dimension_comparison', label: 'Assessing risk controls', status: 'pending' },
  { id: 'risk_question_merge', label: 'Consolidating questions', status: 'pending' },
  { id: 'risk_document_resolution', label: 'Resolving via documents', status: 'pending' },
  { id: 'risk_re_extraction', label: 'Auto-extracting answers', status: 'pending' },
  { id: 'hitl', label: 'Awaiting questionnaire', status: 'pending' },
  { id: 'risk_dimension_analysis', label: 'Scoring risk dimensions', status: 'pending' },
  { id: 'risk_score_aggregation', label: 'Calculating risk scores', status: 'pending' },
];

const INITIAL_STATE: RiskSocketState = {
  stage: 'idle',
  stageMessage: '',
  sessionId: '',
  stepperNodes: DEFAULT_STEPPER.map(n => ({ ...n })),
  areasProcessed: [],
  dimensionsExtracted: 0,
  comparisonProgress: '',
  totalDimensions: 0,
  questionsCount: 0,
  questions: [],
  finalResult: null,
  error: null,
  eventLog: [],
  nodeStats: {},
  attempts: 0,
  maxAttempts: 3,
  retryAfter: null,
  reconnecting: false,
};
const ACTIVE_KEY = 'risk:active_session';

const API_BASE = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000';
const WS_BASE = API_BASE.replace(/^http/, 'ws');
const MAX_LOG = 5;

const updateStepper = (nodes: StepperNode[], nodeId: string, status: 'running' | 'complete' | 'failed'): StepperNode[] =>
  nodes.map(n => n.id === nodeId ? { ...n, status } : n);

/** Rebuild the hook state from GET /risk/status (SRS #122) after a reconnect or page refresh. */
const applySnapshot = (snap: any, prev: RiskSocketState): RiskSocketState => {
  const stepperNodes: StepperNode[] = (snap.stages || []).map((st: any) => ({ id: st.id, label: st.label, status: st.status ?? 'pending', attempts: st.attempts }));
  const status: string = snap.status;
  const stage: RiskStage = status === 'complete' ? 'done' : status === 'failed' ? 'error' : status === 'retry_scheduled' ? 'retry_scheduled'
    : status === 'awaiting_hitl' ? 'awaiting_hitl' : ((snap.current_node as RiskStage) || prev.stage);
  return {
    ...prev,
    sessionId: snap.session_id,
    stage,
    stageMessage: snap.message || prev.stageMessage,
    stepperNodes: stepperNodes.length ? stepperNodes : prev.stepperNodes,
    error: status === 'failed' ? (snap.last_error || snap.message || 'Assessment failed') : null,
    attempts: snap.attempts ?? 0,
    maxAttempts: snap.max_attempts ?? 3,
    retryAfter: snap.retry_after ?? null,
    eventLog: (snap.log || []).slice(-MAX_LOG).map((l: any) => l.message),
  };
};

const addLog = (logs: string[], msg: string): string[] =>
  [...logs.slice(-(MAX_LOG - 1)), msg];

const handleMessage = (msg: any, prev: RiskSocketState): RiskSocketState => {
  const event = msg.event as string;

  switch (event) {
    case 'session':
      try { localStorage.setItem(ACTIVE_KEY, msg.session_id); } catch { /* ignore */ }
      return { ...prev, sessionId: msg.session_id };

    case 'node_retry':
      return {
        ...prev,
        stageMessage: msg.message ?? 'Retrying…',
        stepperNodes: prev.stepperNodes.map(n => n.id === msg.node ? { ...n, attempts: msg.attempt } : n),
        eventLog: addLog(prev.eventLog, msg.message ?? `${msg.node}: retry ${msg.attempt}`),
      };

    case 'retry_scheduled':
      return {
        ...prev,
        stage: 'retry_scheduled',
        stageMessage: msg.message ?? 'Retrying automatically…',
        attempts: msg.attempts ?? prev.attempts,
        maxAttempts: msg.max ?? prev.maxAttempts,
        retryAfter: msg.retry_after ?? null,
        eventLog: addLog(prev.eventLog, msg.message ?? 'Retry scheduled'),
      };

    case 'node_started': {
      const nodeId = msg.node as string;
      const stageMap: Record<string, RiskStage> = {
        risk_company_extraction: 'risk_company_extraction',
        risk_dimension_extraction: 'risk_dimension_extraction',
        risk_dimension_merge: 'risk_dimension_merge',
        risk_priority_filter: 'risk_priority_filter',
        risk_dimension_comparison: 'risk_dimension_comparison',
        risk_question_merge: 'risk_question_merge',
        risk_document_resolution: 'risk_document_resolution',
        risk_re_extraction: 'risk_re_extraction',
        risk_dimension_analysis: 'risk_dimension_analysis',
        risk_score_aggregation: 'risk_score_aggregation',
      };
      return {
        ...prev,
        stage: stageMap[nodeId] ?? prev.stage,
        stageMessage: msg.message ?? '',
        stepperNodes: updateStepper(prev.stepperNodes, nodeId, 'running'),
        eventLog: addLog(prev.eventLog, msg.message ?? `${nodeId} started`),
      };
    }

    case 'node_complete': {
      const nodeId = msg.node as string;
      const updated = updateStepper(prev.stepperNodes, nodeId, 'complete');
      const isDone = nodeId === 'risk_score_aggregation';
      const newStats = { ...prev.nodeStats };
      if (typeof msg.done === 'number') {
        newStats[nodeId] = { done: msg.done, total: msg.total ?? msg.done, extra: msg.extra };
      }
      return {
        ...prev,
        stage: isDone ? 'done' : prev.stage,
        stageMessage: msg.message ?? '',
        stepperNodes: updated,
        eventLog: addLog(prev.eventLog, msg.message ?? `${nodeId} complete`),
        nodeStats: newStats,
      };
    }

    case 'area_processed':
      return {
        ...prev,
        areasProcessed: [...prev.areasProcessed, { area: msg.area, cached: msg.cached }],
        eventLog: addLog(prev.eventLog, `Area: ${msg.area} ${msg.cached ? '(cached)' : ''}`),
      };

    case 'dimension_extracted':
      return {
        ...prev,
        dimensionsExtracted: prev.dimensionsExtracted + (msg.count ?? 0),
        eventLog: addLog(prev.eventLog, `Extracted ${msg.count} risk dimensions from ${msg.policy_id?.slice(0, 8)}...`),
      };

    case 'comparison_started':
      return { ...prev, totalDimensions: msg.total_dimensions ?? 0 };

    case 'dimension_compared':
      return {
        ...prev,
        comparisonProgress: msg.progress ?? '',
        eventLog: addLog(prev.eventLog, `Compared ${msg.progress}: ${msg.matched ? 'mitigated' : 'risk found'}`),
      };

    case 'questions_ready':
      return {
        ...prev,
        stage: 'awaiting_hitl',
        questionsCount: msg.count ?? 0,
        stepperNodes: updateStepper(prev.stepperNodes, 'hitl', 'running'),
        eventLog: addLog(prev.eventLog, `${msg.count} questions need your input`),
      };

    case 'done':
      try { localStorage.removeItem(ACTIVE_KEY); } catch { /* ignore */ }
      return {
        ...prev,
        stage: 'done',
        stageMessage: 'Risk assessment complete',
        finalResult: msg.final_result ?? prev.finalResult,
        eventLog: addLog(prev.eventLog, 'Risk assessment complete'),
      };

    case 'error':
      return {
        ...prev,
        stage: 'error',
        error: msg.message ?? 'Unknown error',
        eventLog: addLog(prev.eventLog, `Error: ${msg.message}`),
      };

    default:
      return prev;
  }
};

// ── Hook ───────────────────────────────────────────────────────────────────────

export function useRiskSocket() {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectCountRef = useRef(0);
  const pollRef = useRef<number | null>(null);
  const [state, setState] = useState<RiskSocketState>({ ...INITIAL_STATE, stepperNodes: DEFAULT_STEPPER.map(n => ({ ...n })) });

  const stopPolling = useCallback(() => {
    if (pollRef.current) { window.clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const startPolling = useCallback((sessionId: string) => {
    stopPolling();
    const tick = async () => {
      const token = localStorage.getItem('policyora_token');
      try {
        const res = await fetch(`${API_BASE}/api/v1/risk/status/${sessionId}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (!res.ok) { if (res.status === 404) stopPolling(); return; }
        const snap = await res.json();
        setState(prev => applySnapshot(snap, { ...prev, reconnecting: !snap.terminal && snap.status !== 'awaiting_hitl' }));
        if (snap.terminal || snap.status === 'awaiting_hitl') {
          stopPolling();
          if (snap.terminal) { try { localStorage.removeItem(ACTIVE_KEY); } catch { /* ignore */ } }
        }
      } catch { /* transient; try again next tick */ }
    };
    tick();
    pollRef.current = window.setInterval(tick, 3000);
  }, [stopPolling]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  /** After a refresh: pick up the assessment that was running (SRS #122). */
  const resumePolling = useCallback((sessionId?: string) => {
    const id = sessionId || (() => { try { return localStorage.getItem(ACTIVE_KEY) || ''; } catch { return ''; } })();
    if (!id) return false;
    setState(prev => ({ ...prev, sessionId: id, reconnecting: true }));
    startPolling(id);
    return true;
  }, [startPolling]);

  /** Ask for another attempt now (SRS #123). */
  const retryNow = useCallback(async (sessionId: string) => {
    const token = localStorage.getItem('policyora_token');
    const res = await fetch(`${API_BASE}/api/v1/risk/retry/${sessionId}`, { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(`Retry failed (${res.status})`);
    const snap = await res.json();
    setState(prev => ({ ...applySnapshot(snap, prev), error: null }));
    startPolling(sessionId);
  }, [startPolling]);

  const reset = useCallback(() => {
    wsRef.current?.close();
    wsRef.current = null;
    stopPolling();
    try { localStorage.removeItem(ACTIVE_KEY); } catch { /* ignore */ }
    setState({ ...INITIAL_STATE, stepperNodes: DEFAULT_STEPPER.map(n => ({ ...n })) });
  }, [stopPolling]);

  const connectWS = useCallback((url: string, onOpen?: (ws: WebSocket) => void) => {
    wsRef.current?.close();

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      reconnectCountRef.current = 0;
      onOpen?.(ws);
    };

    ws.onmessage = (ev: MessageEvent) => {
      let msg: any;
      try { msg = JSON.parse(ev.data); } catch { return; }
      setState(prev => handleMessage(msg, prev));
    };

    ws.onerror = () => {
      setState(prev => {
        if (prev.sessionId) return prev;
        return { ...prev, stage: 'error', error: 'WebSocket connection failed.' };
      });
    };

    ws.onclose = () => {
      wsRef.current = null;
      // The run continues server-side (SRS #122): keep following it by polling.
      setState(prev => {
        if (prev.sessionId && prev.stage !== 'done' && prev.stage !== 'error' && prev.stage !== 'awaiting_hitl') {
          startPolling(prev.sessionId);
          return { ...prev, reconnecting: true };
        }
        return prev;
      });
    };
  }, [startPolling]);

  const start = useCallback((companyId: string, policyIds: string[], token: string, jurisdiction?: string) => {
    setState({ ...INITIAL_STATE, stepperNodes: DEFAULT_STEPPER.map(n => ({ ...n })) });
    const url = `${WS_BASE}/api/v1/risk/ws?token=${encodeURIComponent(token)}`;
    connectWS(url, (ws) => {
      ws.send(JSON.stringify({ company_id: companyId, policy_ids: policyIds, jurisdiction: jurisdiction || '' }));
    });
  }, [connectWS]);

  const resume = useCallback((sessionId: string, token: string) => {
    const url = `${WS_BASE}/api/v1/risk/ws/resume?token=${encodeURIComponent(token)}`;
    setState(prev => ({
      ...prev,
      stage: 'risk_dimension_analysis',
      stageMessage: 'Resuming risk assessment with your answers...',
      stepperNodes: updateStepper(
        updateStepper(prev.stepperNodes, 'hitl', 'complete'),
        'risk_dimension_analysis', 'running',
      ),
    }));
    connectWS(url, (ws) => {
      ws.send(JSON.stringify({ session_id: sessionId }));
    });
  }, [connectWS]);

  const setQuestions = useCallback((questions: RiskQuestion[]) => {
    setState(prev => ({ ...prev, questions }));
  }, []);

  const setFinalResult = useCallback((result: RiskFinalResult) => {
    setState(prev => ({ ...prev, finalResult: result }));
  }, []);

  const disconnect = useCallback(() => {
    wsRef.current?.close();
    wsRef.current = null;
  }, []);

  return { state, start, resume, reset, disconnect, setQuestions, setFinalResult, resumePolling, retryNow };
}
